import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, Heart } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { buildPlan } from "@/lib/journeyEngine";
import { track } from "@/lib/api";
import { ServiceCard } from "@/components/site/ServiceCard";
import { Linkify } from "@/components/site/Linkify";
import { PlanDownload } from "./PlanDownload";
import { JourneyFeedback } from "./JourneyFeedback";

const CtaLink = ({ to, children }) => {
  const cls = "mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-cream px-6 py-3 min-h-[48px] font-semibold text-teal hover:bg-white transition-colors w-full sm:w-auto";
  if (/^(tel|sms):/.test(to)) return <a href={to} className={cls} data-testid="journey-next-step-cta">{children}</a>;
  if (to.startsWith("http")) return <a href={to} target="_blank" rel="noopener noreferrer" className={cls} data-testid="journey-next-step-cta">{children}<span className="sr-only">(opens in a new tab)</span></a>;
  return <Link to={to} className={cls} data-testid="journey-next-step-cta">{children}</Link>;
};

const Bullets = ({ items, ordered }) => {
  const Tag = ordered ? "ol" : "ul";
  return <Tag className={`space-y-3 pl-5 ${ordered ? "list-decimal" : "list-disc"} marker:text-sage-dark text-ink-soft`}>{items.map((t) => <li key={t}>{t}</li>)}</Tag>;
};

const Section = ({ value, title, children }) => (
  <AccordionItem value={value} className="border-cream-deep" data-testid={`journey-section-${value}`}>
    <AccordionTrigger className="text-left font-serif text-xl sm:text-2xl py-5 hover:no-underline" data-testid={`journey-section-${value}-trigger`}>{title}</AccordionTrigger>
    <AccordionContent className="text-base pb-6">{children}</AccordionContent>
  </AccordionItem>
);

const Support = ({ plan }) => (
  <section aria-labelledby="journey-support-heading" data-testid="journey-support-section">
    <h2 id="journey-support-heading" className="text-2xl sm:text-3xl">Where you can turn</h2>
    <p className="mt-2 text-ink-soft">These are independent UK organisations. Addiction &amp; Us doesn't run a helpline, counselling or live chat.</p>
    <div className="mt-5 grid gap-4 md:grid-cols-2">{plan.services.map((s) => <ServiceCard key={s.slug} entry={s} />)}</div>
    {plan.notes.length > 0 && <div className="mt-4 rounded-2xl bg-teal-light p-5 space-y-2" data-testid="journey-notes">{plan.notes.map((n) => <p key={n}><Linkify text={n} /></p>)}</div>}
    <p className="mt-4 text-sm text-ink-soft" data-testid="journey-directory-note"><Link to="/directory" className="btn-ghost !inline">See all UK support organisations</Link></p>
  </section>
);

const Related = ({ items }) => items.length > 0 && (
  <section aria-labelledby="related-journeys-heading" data-testid="journey-related">
    <h2 id="related-journeys-heading" className="text-2xl sm:text-3xl">You might also find this helpful</h2>
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      {items.map((r) => (
        <Link key={r.slug} to={`/find-your-way/${r.slug}`} className="group flex items-center justify-between gap-3 rounded-2xl border-2 border-cream-deep bg-white/80 px-5 py-4 min-h-[64px] font-medium hover:border-teal transition-colors" data-testid={`journey-related-${r.slug}`}>
          {r.label}<ArrowRight className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" aria-hidden="true" />
        </Link>
      ))}
    </div>
  </section>
);

const COUNTED = new Set();

export const JourneyResults = ({ journey, answers, onRestart, onBack, headingRef }) => {
  const plan = useMemo(() => buildPlan(journey, answers), [journey, answers]);
  const banner = plan.safety[0] && journey.safety_panels[plan.safety[0]];
  useEffect(() => {
    if (!COUNTED.has(journey.slug)) { COUNTED.add(journey.slug); track("journey_complete", journey.slug); }
  }, [journey.slug]);
  return (
    <div className="container-site max-w-3xl pt-8 pb-8 space-y-10" data-testid="journey-results">
      <button type="button" onClick={onBack} className="btn-ghost text-sm" data-testid="journey-results-back">Change my answers</button>
      {banner && (
        <div className={`flex gap-3 rounded-2xl p-4 font-semibold ${banner.tone === "urgent" ? "bg-clay-dark text-white" : "bg-[#FFF4E5] text-[#6B4100]"}`} role="alert" data-testid="journey-results-safety-banner">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" /><p><Linkify text={banner.banner} /></p>
        </div>
      )}
      <header>
        <p className="eyebrow mb-3">Your next steps</p>
        <h1 ref={headingRef} tabIndex={-1} className="focus-visible:outline-none text-4xl sm:text-5xl leading-[1.05]">Here's a way forward.</h1>
        <p className="mt-4 text-lg text-ink-soft" data-testid="journey-results-ack">{plan.ack}</p>
      </header>
      <section className="rounded-[2rem] bg-teal text-cream p-7 sm:p-9" data-testid="journey-next-step-card">
        <p className="eyebrow !text-cream/75">One thing you can do today</p>
        <h2 className="mt-2 text-3xl !text-cream">{plan.today.title}</h2>
        <p className="mt-3 text-cream/90 text-lg">{plan.today.text}</p>
        <CtaLink to={plan.today.cta_to}>{plan.today.cta_label} <ArrowRight className="h-5 w-5" aria-hidden="true" /></CtaLink>
      </section>
      {plan.supportFirst && <Support plan={plan} />}
      <Accordion type="multiple" defaultValue={["matters"]} className="card-soft !bg-white px-6 sm:px-8">
        <Section value="matters" title="What matters right now"><Bullets items={plan.matters} /></Section>
        <Section value="actions" title="More things you could try"><Bullets items={plan.actions} ordered /></Section>
        <Section value="wait" title="What can wait"><Bullets items={plan.wait} /></Section>
        <Section value="understanding" title="Understanding what's happening">
          <ul className="space-y-3">{plan.articles.map((a) => <li key={a.slug}><Link to={`/${a.hub}/${a.slug}`} className="btn-ghost" data-testid={`journey-article-${a.slug}`}>{a.title}</Link><p className="text-sm text-ink-muted mt-0.5">{a.summary}</p></li>)}</ul>
        </Section>
      </Accordion>
      {plan.youMatter && (
        <section className="flex gap-4 rounded-[2rem] bg-sage-light p-6 sm:p-8" data-testid="journey-you-matter">
          <Heart className="h-6 w-6 text-sage-dark shrink-0 mt-1" aria-hidden="true" />
          <div><h2 className="text-2xl">You matter too</h2><p className="mt-2 text-lg text-ink-soft">{plan.youMatter}</p></div>
        </section>
      )}
      {!plan.supportFirst && <Support plan={plan} />}
      <Related items={plan.related} />
      <JourneyFeedback slug={journey.slug} />
      <PlanDownload journey={journey} answers={answers} onRestart={onRestart} />
    </div>
  );
};
